import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import { cn } from '@/lib/utils';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useDashboardRecentOrdersApi } from '@/features/dashboard/hooks/useDashboardRecentOrdersApi';
import { ordersUtils } from '@/features/orders/utils/ordersUtils';
import DashboardOrdersEmpty from '@/features/dashboard/components/DashboardOrders/DashboardOrdersEmpty';
import OrderDetails from '@/features/orders/components/OrderDetails';

type Props = {
	isMobile?: boolean;
};

const DashboardOrders: FC<Props> = ({ isMobile }) => {
	const [showDetails, setShowDetails] = useState(false);
	const [orderId, setOrderId] = useState('');

	const navigate = useNavigate();
	const { t } = useTranslation();

	const { recentOrdersData, isLoadingRecentOrdersData } =
		useDashboardRecentOrdersApi();

	const handleShowOrderDetails = (id: string) => {
		setOrderId(id);
		setShowDetails(true);
	};

	return (
		<div className='grow flex flex-col gap-4'>
			<div className='flex-1'>
				{isLoadingRecentOrdersData && (
					<EActivitiesListSkeleton isMobile={isMobile} />
				)}
				{!isLoadingRecentOrdersData &&
					!!recentOrdersData?.groups?.length && (
						<div
							className={cn(
								'flex flex-col gap-6',
								isMobile && 'gap-4',
							)}>
							{recentOrdersData?.groups.map(
								({ date, orders }) => {
									return (
										<div key={`${date}`}>
											<h5 className='text-sm text-popover-800'>
												{DateTimeHelper.formatDate(
													new Date(date),
													2,
												)}
											</h5>
											{orders?.map((order) => {
												const {
													label,
													icon,
													colorClass,
												} = ordersUtils.getStatusData(
													order.status,
												);

												return (
													<EActivitiesElement
														key={order.id}
														icon={icon}
														colorClass={colorClass}
														header={
															order.services ??
															t(
																'main.dashboard.components.recentActivities.list.orders.noServices',
															)
														}
														description={label}
														value={
															order.totalAmount
														}
														id={order.id}
														onClick={
															handleShowOrderDetails
														}
														isMobile={isMobile}
														currencyAssetType='other'
													/>
												);
											})}
										</div>
									);
								},
							)}
						</div>
					)}
				{!isLoadingRecentOrdersData &&
					!recentOrdersData?.groups?.length && (
						<DashboardOrdersEmpty />
					)}
			</div>
			<Button
				variant={'outline'}
				className='w-full text-sm font-medium'
				onClick={() => navigate('/orders')}>
				{t(
					'main.dashboard.components.recentActivities.list.orders.seeMoreButton',
				)}
			</Button>
			{showDetails && (
				<OrderDetails
					id={orderId}
					onClose={() => setShowDetails(false)}
					isMobile={isMobile}
				/>
			)}
		</div>
	);
};

export default DashboardOrders;
